import axios from './axios';

// Thin wrappers over the admin follow-up endpoints (/admin/follow-ups/*).
// All calls go through the shared axios instance (token + global notifications).
// Views render their own snackbars, so mutations pass { skipNotify: 'success' }.

export const fetchFollowUps = (params) => axios.get('/admin/follow-ups', { params });

export const fetchFollowUpStats = () => axios.get('/admin/follow-ups/stats', { skipNotify: true });

// Returns a CSV blob of the whole current segment (all matching users, not just
// the current page). Honours the same segment/search/status filters.
export const exportFollowUpsCsv = (params) =>
  axios.get('/admin/follow-ups/export', { params, responseType: 'blob', skipNotify: true });

export const saveFollowUp = (userId, body) =>
  axios.put(`/admin/follow-ups/${userId}`, body, { skipNotify: 'success' });

export const logFollowUpContact = (userId, method, note) =>
  axios.post(`/admin/follow-ups/${userId}/contact`, { method, note }, { skipNotify: 'success' });

export const sendFollowUpEmail = (userId, subject, message) =>
  axios.post(`/admin/follow-ups/${userId}/email`, { subject, message }, { skipNotify: 'success' });

export const sendFollowUpWhatsApp = (userId, message) =>
  axios.post(`/admin/follow-ups/${userId}/whatsapp`, { message }, { skipNotify: 'success' });

export const recordFollowUpOpen = (userId) =>
  axios.post(`/admin/follow-ups/${userId}/open`, {}, { skipNotify: true });

export const removeFollowUp = (userId) =>
  axios.delete(`/admin/follow-ups/${userId}`, { skipNotify: 'success' });

export const deleteFollowUpUser = (userId) =>
  axios.delete(`/admin/follow-ups/${userId}/user`, { skipNotify: 'success' });

// ── Shared labels/enums (mirror src/model/followUp.model.js) ─────────────────
export const SEGMENTS = [
  { key: 'all', label: 'All' },
  { key: 'unverified', label: 'Unverified' },
  { key: 'business_pending', label: 'Business Pending' },
  { key: 'product_pending', label: 'Product Pending' },
];

export const SEGMENT_LABEL = {
  unverified: 'Unverified',
  business_pending: 'Business Pending',
  product_pending: 'Product Pending',
  active: 'Onboarded',
};

export const SEGMENT_COLORS = {
  unverified: { bg: 'rgba(244,67,54,0.12)', fg: '#c62828' },
  business_pending: { bg: 'rgba(255,152,0,0.14)', fg: '#b26a00' },
  product_pending: { bg: 'rgba(63,81,181,0.12)', fg: '#3949ab' },
  active: { bg: 'rgba(0,137,123,0.12)', fg: '#00897b' },
};

export const FOLLOWUP_STATUSES = [
  { key: 'pending', label: 'Pending' },
  { key: 'attempted', label: 'Attempted' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'converted', label: 'Converted' },
  { key: 'not_interested', label: 'Not Interested' },
  { key: 'invalid', label: 'Invalid / Bad Lead' },
];

export const STATUS_COLORS = {
  pending: { bg: 'rgba(158,158,158,0.16)', fg: '#616161' },
  attempted: { bg: 'rgba(255,152,0,0.14)', fg: '#b26a00' },
  contacted: { bg: 'rgba(3,169,244,0.12)', fg: '#0277bd' },
  in_progress: { bg: 'rgba(63,81,181,0.12)', fg: '#3949ab' },
  converted: { bg: 'rgba(0,137,123,0.12)', fg: '#00897b' },
  not_interested: { bg: 'rgba(244,67,54,0.12)', fg: '#c62828' },
  invalid: { bg: 'rgba(97,97,97,0.16)', fg: '#424242' },
};

export const FOLLOWUP_REASONS = [
  { key: 'not_reachable', label: 'Phone not reachable / no answer' },
  { key: 'wrong_number', label: 'Wrong or invalid number' },
  { key: 'awaiting_response', label: 'Contacted, awaiting response' },
  { key: 'needs_onboarding_help', label: 'Needs help completing setup' },
  { key: 'language_barrier', label: 'Language barrier' },
  { key: 'price_concern', label: 'Price / plan concern' },
  { key: 'not_interested', label: 'Not interested right now' },
  { key: 'using_competitor', label: 'Using a competitor' },
  { key: 'will_do_later', label: 'Will complete later' },
  { key: 'duplicate_or_test', label: 'Duplicate / test account' },
  { key: 'other', label: 'Other (see notes)' },
];

export const DUE_FILTERS = [
  { key: '', label: 'All schedules' },
  { key: 'overdue', label: 'Overdue follow-ups' },
  { key: 'today', label: 'Due today' },
  { key: 'scheduled', label: 'Any scheduled date' },
  { key: 'never_contacted', label: 'Never contacted' },
];

export const SORT_OPTIONS = [
  { key: 'created_desc', label: 'Newest registered' },
  { key: 'created_asc', label: 'Oldest registered' },
  { key: 'next_asc', label: 'Next follow-up (earliest)' },
  { key: 'last_contacted_desc', label: 'Recently contacted' },
  { key: 'contact_count_desc', label: 'Most touches' },
  { key: 'open_count_desc', label: 'Most opened' },
];

export const labelOf = (list, key) => list.find((x) => x.key === key)?.label || key;
